import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faXmark } from "@fortawesome/free-solid-svg-icons";

import { PrimaryButton, SubtleButton } from "../button/Button.jsx";
import { ProgressBar } from "../progress/Progress.jsx";

import {
    TOTAL_ONBOARDING_STEPS, gameEntryPath, isOnboardingPath, stepIndex,
} from "../../routes/onboardingSteps.js";

import "./OnboardingBanner.css";

const CLOSED_KEY = 'gp_setup_banner_closed'; // per-session (sessionStorage)

const isClosed = () => {
    try { return sessionStorage.getItem(CLOSED_KEY) === '1'; } catch (_) { return false; }
};

// This banner is the first-run nudge, so it tracks the FIRST game only: the org's single game,
// still DRAFT (its wizard hasn't finished). Once that game goes ACTIVE onboarding is done, and
// every game added afterwards gets the wizard and the draft badge but no banner.
const firstGameInSetup = (games) => {
    const list = Array.isArray(games) ? games : [];
    if (list.length !== 1) return null;
    return list[0]?.state === 'DRAFT' ? list[0] : null;
};

const OnboardingBanner = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userGames = useSelector(s => s.userGames);
    const [closed, setClosed] = useState(isClosed);

    const game = firstGameInSetup(userGames);
    if (!game) return null;
    // The wizard shows its own progress rail — don't stack the banner on top of it.
    if (isOnboardingPath(location.pathname)) return null;
    if (closed) return null;

    const handleClose = () => {
        try { sessionStorage.setItem(CLOSED_KEY, '1'); } catch (_) { /* noop */ }
        setClosed(true);
    };

    // setupStep is the step the user is ON, so everything before it is done.
    const savedStep = game.profileSetup?.setupStep;
    const idx = Math.max(stepIndex(savedStep), 0);
    const remaining = TOTAL_ONBOARDING_STEPS - idx;
    const pct = Math.round((idx / TOTAL_ONBOARDING_STEPS) * 100);

    return (
        <div className="onboarding-banner-container">
            <div className="onboarding-banner" role="region" aria-label="Game setup progress">
                <div className="onboarding-banner-text">
                    <span className="onboarding-banner-title">
                        Finish setting up {game.gameName || 'your game'}
                    </span>
                    <span className="onboarding-banner-meta">
                        <strong>{idx} of {TOTAL_ONBOARDING_STEPS}</strong> done — {remaining} step{remaining === 1 ? '' : 's'} left.
                    </span>
                </div>
                <div className="onboarding-banner-progress">
                    <ProgressBar value={pct} />
                </div>
                <PrimaryButton
                    size="small"
                    icon={faArrowRight}
                    iconPosition="right"
                    onClick={() => navigate(gameEntryPath(game))}
                >
                    Continue Setup
                </PrimaryButton>
                <SubtleButton size="small" onClick={handleClose} aria-label="Dismiss">
                    <FontAwesomeIcon icon={faXmark} />
                </SubtleButton>
            </div>
        </div>
    );
};

export default OnboardingBanner;
